import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import moment from 'moment';

@Component({
  selector: 'app-time-picker',
  templateUrl: './time-picker.component.html',
  styleUrls: ['./time-picker.component.css'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => TimePickerComponent),
    multi: true,
  }],
})
export class TimePickerComponent implements ControlValueAccessor, OnChanges {
  @Input() name: string = '';

  @Input() stepMinutes: number = 5;
  @Input() startHour: number = 0;
  @Input() endHour: number = 23;

  // Disable specific HH:mm values
  @Input() disabledTimes: string[] = [];

  disabled: boolean = false;
  timeOptions: string[] = [];
  timeValue: string | null = null;

  private disabledTimesSet: Set<string> = new Set<string>();

  private onChangeCallback: (_: any) => void = () => {};
  private onTouchedCallback: () => void = () => {};

  constructor() {
    this.buildOptions();
    this.rebuildDisabledTimesSet();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['stepMinutes'] || changes['startHour'] || changes['endHour']) {
      this.buildOptions();
    }

    if (changes['disabledTimes']) {
      this.rebuildDisabledTimesSet();
    }
  }

  writeValue(value: any): void {
    if (value === null || value === undefined || value === '') {
      this.timeValue = null;
      return;
    }

    if (value instanceof Date) {
      const m = moment(value);
      this.timeValue = m.isValid() ? m.format('HH:mm') : null;
      return;
    }

    if (typeof value === 'string') {
      const m = moment(value, 'HH:mm', true);
      this.timeValue = m.isValid() ? m.format('HH:mm') : null;
      return;
    }

    this.timeValue = null;
  }

  registerOnChange(fn: (_: any) => {}): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onSelectChange(newValue: string | null): void {
    this.timeValue = newValue || null;
    this.onChangeCallback(this.timeValue);
    this.onTouchedCallback();
  }

  isOptionDisabled(option: string): boolean {
    return this.disabledTimesSet.has(option);
  }

  private buildOptions(): void {
    const options: string[] = [];

    const safeStart = Math.max(0, Math.floor(this.startHour));
    const safeEnd = Math.min(23, Math.floor(this.endHour));
    const step = Math.max(1, Math.floor(this.stepMinutes));

    for (let hour = safeStart; hour <= safeEnd; hour++) {
      for (let minutes = 0; minutes < 60; minutes += step) {
        const hourStr = hour.toString().padStart(2, '0');
        const minuteStr = minutes.toString().padStart(2, '0');
        options.push(`${hourStr}:${minuteStr}`);
      }
    }

    this.timeOptions = options;
  }

  private rebuildDisabledTimesSet(): void {
    const set = new Set<string>();

    if (Array.isArray(this.disabledTimes)) {
      for (const value of this.disabledTimes) {
        if (typeof value === 'string' && value.length > 0) {
          set.add(value);
        }
      }
    }

    this.disabledTimesSet = set;
  }
}
